import * as logger from "common/logger";
import { useState, useEffect } from "react";

/**
 * useFeedbackLoader - React hook that fetches all feedback pages progressively
 *
 * Fetches all pages on mount, updating state progressively as each page arrives.
 * Uses overlap and deduplication to protect against concurrent updates.
 *
 * @param {Function} listFeedback - Async function that fetches feedback data
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Number of items per page (default: 50)
 * @param {number} options.overlap - Number of overlapping items between pages (default: 5)
 *
 * @returns {Object} - { data, isLoading }
 */
export function useFeedbackLoader(listFeedback, { pageSize = 50 } = {}) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchAllPages = async () => {
      // We don't have an iterator pagination. So if feedback is added while we are scanning through the pages
      // the pages the page numbering can get shifted. Because we are sorting newest to oldest, this will mean
      // seeing the same item again. So we need to deduplicate results but we don't need to worry about missing
      // items.

      const seenIds = new Set();
      const allData = [];
      let skip = 0;
      let hasMore = true;

      while (hasMore && !cancelled) {
        try {
          const result = await listFeedback({
            skip,
            limit: pageSize,
          }).unwrap();

          const uniqueNew = result.filter((item) => {
            if (seenIds.has(item.feedbackId)) {
              return false;
            }
            seenIds.add(item.feedbackId);
            return true;
          });

          allData.push(...uniqueNew);

          // Update state progressively so UI shows results as they arrive
          if (!cancelled) {
            setData([...allData]);
          }

          skip += pageSize;

          if (result.length < pageSize) {
            hasMore = false;
          }
        } catch (error) {
          logger.error("Error fetching feedback:", error);
          hasMore = false;
        }
      }

      if (!cancelled) {
        setIsLoading(false);
      }
    };

    fetchAllPages();

    return () => {
      cancelled = true;
    };
  }, [listFeedback, pageSize]);

  return {
    data,
    isLoading,
  };
}
